import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useUpload } from '../../hooks/useUpload.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';
import {
  getNormatividadCategoryLabel,
  NORMATIVIDAD_CATEGORIES,
} from '../../constants/normatividadCategories.js';

const empty = {
  titulo: '',
  categoria: 'reglamentos',
  documento: '',
  fecha_actualizacion: new Date().toISOString().slice(0, 10),
  orden: 0,
  visible: true,
};

export default function NormatividadAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const { uploadDocument, uploading } = useUpload();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  const documentoPath = watch('documento');

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/normatividad', {
        params: { page, per_page: perPage, search: debouncedSearch },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  function openCreate() {
    setEditingId(null);
    reset(empty);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    reset({
      titulo: row.titulo ?? '',
      categoria: row.categoria ?? 'reglamentos',
      documento: row.documento ?? '',
      fecha_actualizacion: String(row.fecha_actualizacion || '').slice(0, 10) || empty.fecha_actualizacion,
      orden: Number(row.orden) || 0,
      visible: Number(row.eliminado) === 0,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      titulo: values.titulo,
      categoria: values.categoria,
      documento: values.documento,
      fecha_actualizacion: values.fecha_actualizacion,
      orden: Number(values.orden) || 0,
      eliminado: values.visible ? 0 : 1,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/v1/normatividad/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/normatividad', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete('¿Ocultar documento del portal?', row.titulo);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/normatividad/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Ocultado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function toggleVisible(row) {
    try {
      const current = await api.get(`/v1/normatividad/${row.id}`);
      const r = current.data?.data;
      if (!r) throw new Error('No se pudo leer el registro');
      const { data } = await api.put(`/v1/normatividad/${row.id}`, {
        titulo: r.titulo,
        categoria: r.categoria ?? 'reglamentos',
        documento: r.documento,
        fecha_actualizacion: String(r.fecha_actualizacion).slice(0, 10),
        orden: Number(r.orden) || 0,
        eliminado: Number(r.eliminado) === 0 ? 1 : 0,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Visibilidad actualizada', '');
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function onPickDocument(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadDocument(file);
      setValue('documento', path, { shouldValidate: true });
      toastSuccess('Documento listo', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  const columns = useMemo(
    () => [
      { key: 'orden', label: 'Orden' },
      {
        key: 'categoria',
        label: 'Categoría',
        render: (row) => getNormatividadCategoryLabel(row.categoria),
      },
      { key: 'titulo', label: 'Título' },
      {
        key: 'fecha_actualizacion',
        label: 'Actualización',
        render: (row) => String(row.fecha_actualizacion || '').slice(0, 10),
      },
      {
        key: 'documento',
        label: 'Archivo',
        render: (row) => (row.documento ? 'Sí' : '—'),
      },
      {
        key: 'eliminado',
        label: 'Portal',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleVisible(row)}
            className={adminPublishStatusClasses(Number(row.eliminado) === 0)}
          >
            {Number(row.eliminado) === 0 ? 'Visible' : 'Oculto'}
          </button>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Editar"
              onClick={() => openEdit(row)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_DELETE_BTN_CLASS}
              title="Ocultar del portal"
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Normatividad</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Administra documentos PDF o Office visibles en /normatividad. Eliminar oculta el registro del portal.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nuevo documento
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Editar documento' : 'Nuevo documento'}>
        <form className="grid gap-5" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Categoría" error={errors.categoria?.message}>
            <select
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('categoria', { required: 'Requerido' })}
            >
              {NORMATIVIDAD_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Título" error={errors.titulo?.message}>
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('titulo', { required: 'Requerido' })}
            />
          </FormField>
          <FormField label="Fecha de actualización" error={errors.fecha_actualizacion?.message}>
            <input
              type="date"
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('fecha_actualizacion', { required: 'Requerido' })}
            />
          </FormField>
          <FormField label="Orden en listado">
            <input
              type="number"
              min={0}
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('orden', { valueAsNumber: true })}
            />
          </FormField>
          <FormField
            label="Documento"
            hint={uploading ? 'Subiendo…' : 'PDF, Word o Excel'}
            error={errors.documento?.message}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf"
                onChange={onPickDocument}
                disabled={uploading}
              />
              <input
                type="text"
                className="w-full flex-1 rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('documento', { required: editingId ? false : 'Sube un documento' })}
              />
            </div>
          </FormField>
          {documentoPath ? (
            <a
              href={assetUrl(documentoPath)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-accent-lime hover:text-white"
            >
              Abrir archivo subido
            </a>
          ) : null}
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-accent-lime-dark focus:ring-brand-green" {...register('visible')} />
            Visible en el portal público
          </label>
          <div className="flex flex-wrap gap-3">
            <Button type="submit">{editingId ? 'Guardar cambios' : 'Crear'}</Button>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa">
        {previewRow && (
          <div className="space-y-4">
            <h3 className="font-display text-2xl font-bold text-white">{previewRow.titulo}</h3>
            <p className="text-sm text-slate-400">
              Categoría: {getNormatividadCategoryLabel(previewRow.categoria)}
            </p>
            <p className="text-sm text-slate-400">
              Actualización: {String(previewRow.fecha_actualizacion || '').slice(0, 10)}
            </p>
            <p className="text-sm text-slate-400">
              Estado: {Number(previewRow.eliminado) === 0 ? 'Visible en portal' : 'Oculto'}
            </p>
            {previewRow.documento ? (
              <a
                href={assetUrl(previewRow.documento)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex text-sm font-semibold text-accent-lime hover:text-white"
              >
                Abrir documento
              </a>
            ) : (
              <p className="text-sm text-slate-500">Sin archivo</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
